import type { FoodBenefitDistribution, FoodBenefitOrder, FoodBenefitRecord } from '../foodBenefitTypes';
import { FOOD_BENEFIT_DISTRIBUTION_KEYS, createEmptyFoodBenefitDistribution } from '../foodBenefitTypes';

const CSV_DELIMITER = ';';

const normalizeValue = (value: string): string => value.trim();

const normalizeHeader = (value: string): string =>
    value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();

const toCurrency = (value: string): number => {
    if (!value) return 0;
    const normalized = value.replace(/\./g, '').replace(',', '.');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeCode = (value: string): string => value.replace(/\D/g, '');

const shouldStopParsing = (line: string): boolean => {
    const normalized = line.toLowerCase();
    return (
        normalized.startsWith('id pedido;') ||
        normalized.startsWith('empresa;') ||
        normalized.startsWith('status do pedido;') ||
        normalized.startsWith('valor total;') ||
        normalized.startsWith('data do pedido;')
    );
};

const splitLine = (line: string): string[] => line.split(CSV_DELIMITER).map((cell) => cell.trim());

const findHeaderIndex = (lines: string[]): number => lines.findIndex((line) => line.toLowerCase().startsWith('cpf;'));

const HEADER_ALIASES: Record<string, keyof FoodBenefitDistribution | 'code' | 'name' | 'total'> = {
    cpf: 'code',
    'nome completo': 'name',
    refeicao: 'meal',
    alimentacao: 'food',
    mobilidade: 'mobility',
    cultura: 'culture',
    saude: 'health',
    educacao: 'education',
    'home office': 'homeOffice',
    'saldo livre': 'freeBalance',
    multi: 'multi',
    total: 'total',
};

const ORDER_ALIASES: Record<string, keyof FoodBenefitOrder> = {
    'id pedido': 'orderId',
    empresa: 'company',
    'status do pedido': 'status',
    'valor total': 'totalAmount',
    'data do pedido': 'orderDate',
};

type HeaderIndexes = {
    code: number;
    name: number;
    total: number;
} & Partial<Record<keyof FoodBenefitDistribution, number>>;

const resolveHeaderIndexes = (headerColumns: string[]): HeaderIndexes => {
    const indexes: HeaderIndexes = {
        code: -1,
        name: -1,
        total: -1,
    };

    headerColumns.forEach((column, index) => {
        const alias = HEADER_ALIASES[normalizeHeader(column)];
        if (!alias) return;
        if (typeof (indexes as any)[alias] === 'number' && (indexes as any)[alias] >= 0) return;
        (indexes as any)[alias] = index;
    });

    if (indexes.code === -1 || indexes.name === -1 || indexes.total === -1) {
        throw new Error('Colunas de CPF, nome ou total não encontradas nos pedidos da Caju.');
    }

    return indexes;
};

const parseOrderFooter = (lines: string[]): FoodBenefitOrder | null => {
    const order: Partial<FoodBenefitOrder> = {};
    lines.forEach((line) => {
        const [rawKey, ...rest] = line.split(CSV_DELIMITER);
        if (!rawKey) return;
        const alias = ORDER_ALIASES[normalizeHeader(rawKey)];
        if (!alias) return;
        const value = rest.join(CSV_DELIMITER).trim();
        if (!value) return;
        if (alias === 'totalAmount') {
            order.totalAmount = toCurrency(value);
        } else if (alias === 'orderDate') {
            order.orderDate = value;
        } else {
            (order as any)[alias] = value;
        }
    });
    if (!order.orderId && !order.totalAmount && !order.company) {
        return null;
    }
    return {
        orderId: order.orderId ?? 'sem-id',
        company: order.company ?? null,
        status: order.status ?? null,
        totalAmount: order.totalAmount ?? 0,
        orderDate: order.orderDate ?? null,
        fileName: null,
    };
};

const parseCsvDataSection = (lines: string[]): { records: FoodBenefitRecord[]; order: FoodBenefitOrder | null } => {
    const headerIndex = findHeaderIndex(lines);
    if (headerIndex === -1) {
        throw new Error('Cabeçalho não identificado nos arquivos da Caju.');
    }

    const headerColumns = splitLine(lines[headerIndex]);
    const indexes = resolveHeaderIndexes(headerColumns);

    const records: FoodBenefitRecord[] = [];
    let footerStartIndex = lines.length;
    for (let i = headerIndex + 1; i < lines.length; i += 1) {
        const line = normalizeValue(lines[i] ?? '');
        if (!line) continue;
        if (shouldStopParsing(line)) {
            footerStartIndex = i;
            break;
        }
        const columns = splitLine(line);
        const rawCode = columns[indexes.code] ?? '';
        const code = normalizeCode(rawCode);
        if (!code) continue;
        const name = columns[indexes.name]?.trim();
        if (!name) continue;

        const distribution = createEmptyFoodBenefitDistribution();
        FOOD_BENEFIT_DISTRIBUTION_KEYS.forEach((key) => {
            const columnIndex = indexes[key];
            if (typeof columnIndex === 'number' && columnIndex >= 0) {
                distribution[key] = toCurrency(columns[columnIndex] ?? '');
            }
        });

        const amount = toCurrency(columns[indexes.total] ?? '');

        const specificBenefitsSum =
            distribution.meal +
            distribution.food +
            distribution.mobility +
            distribution.culture +
            distribution.health +
            distribution.education +
            distribution.homeOffice +
            distribution.multi;

        if (specificBenefitsSum === 0 && amount > 0) {
            distribution.freeBalance = amount;
        }

        records.push({
            ...distribution,
            code,
            name,
            dependentsCount: 0,
            feeCount: 1,
            amount,
        });
    }

    const footer = parseOrderFooter(lines.slice(footerStartIndex));
    return { records, order: footer };
};

export async function parseCajuFile(file: File): Promise<{ records: FoodBenefitRecord[]; order: FoodBenefitOrder | null }> {
    const text = await file.text();
    const normalized = text.replace(/\r\n/g, '\n');
    const lines = normalized.split('\n').map((line) => line.replace(/^\uFEFF/, '').trim());

    try {
        return parseCsvDataSection(lines);
    } catch (error) {
        console.error('[parseCajuFile] failed to parse file', file.name, error);
        throw error;
    }
}
